import { apiCall } from ".";
import { getSession } from "../handlers/getSession";
import { getTenant } from "../handlers/getTenant";

/**
 * EQL — Ethics Query Language.
 * POST /{tenant}/api/reports/eql/          → query (raw rows) or aggregate
 * GET  /{tenant}/api/reports/eql/catalogues/ → catalogs usable as dimensions
 *
 * Two contract quirks are encapsulated here so callers never trip on them:
 *
 *  1. Field notation differs by location. In `fields` / `aggregate.group_by`
 *     plain fields are validated WITHOUT splitting on `__`, so nested JSON is
 *     addressed with a dot: `status.current`. In `filters` the parser DOES
 *     split on `__`, so the same data is addressed `status__current`.
 *     Bracket extraction (`metadata[C::OWN::x]`) works in fields/group_by but
 *     is NOT allowed in filters — filter metadata with `jsonb_contains`.
 *
 *  2. The route is `reports` (plural), unlike `/{tenant}/api/report/list/`.
 *
 * Backend limits: `limit` is capped at 1000 (MAX_RESULTS_PER_QUERY) and the
 * aggregate path scans every filtered row in Python (no SQL GROUP BY), so
 * prefer one raw paginated pass over many aggregate calls when crossing dims.
 */

export const EQL_MAX_LIMIT = 1000;

const eqlPath = (tenant, suffix = "") =>
  `${tenant}/api/reports/eql/${suffix}`;

const authHeaders = (session) => ({
  IdToken: session.IdToken,
  Authorization: `Bearer ${session.AccessToken}`,
});

const post = async (body) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.post(eqlPath(tenant), body, {
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * Aggregate (group_by + count). Single grouping axis only — the backend
 * rejects an array `group_by`.
 * @param {string} groupBy  e.g. "status.current" | "metadata[C::OWN::business_units]"
 * @param {number} [depth]  truncates hierarchical `type::a::type::b` values to N levels
 * @param {string[]} [exclude] drops groups whose value startsWith any prefix
 * @param {Object} [filters] EQL filter tree
 * @returns {Promise<{data: Array<{value: string|null, count: number}>, metadata: Object}>}
 */
export const eqlAggregate = async ({ groupBy, depth, exclude, filters }) => {
  const aggregate = { group_by: groupBy, count: true };
  if (depth) aggregate.depth = depth;
  if (exclude?.length) aggregate.exclude = exclude;

  const body = { model: "Report", aggregate };
  if (filters) body.filters = filters;

  return post(body);
};

/**
 * Raw rows. `fields` uses dot/bracket notation (see note above).
 * @returns {Promise<{data: Array<Object>, metadata: {total, limit, offset, count, has_more}}>}
 */
export const eqlQuery = async ({
  fields,
  filters,
  limit = 100,
  offset = 0,
  orderBy,
}) => {
  const body = {
    model: "Report",
    fields,
    limit: Math.min(limit, EQL_MAX_LIMIT),
    offset,
  };
  if (filters) body.filters = filters;
  if (orderBy) body.order_by = orderBy;

  return post(body);
};

/**
 * Raw rows, all pages. Walks offset/limit until `metadata.total` is reached
 * or `max` rows are collected.
 * @returns {Promise<{rows: Array<Object>, total: number, truncated: boolean}>}
 */
export const eqlQueryAll = async ({ fields, filters, max = 5000 }) => {
  const rows = [];
  let offset = 0;
  let total = 0;

  while (rows.length < max) {
    const pageSize = Math.min(EQL_MAX_LIMIT, max - rows.length);
    const resp = await eqlQuery({ fields, filters, limit: pageSize, offset });
    const batch = resp?.data || [];
    total = resp?.metadata?.total ?? rows.length + batch.length;

    rows.push(...batch);

    if (!resp?.metadata?.has_more || batch.length === 0) break;
    offset += batch.length;
  }

  return { rows, total, truncated: rows.length < total };
};

/**
 * Catalogs referenced by the tenant's form (non-sensitive fields only).
 * `full_key` is the literal metadata key, e.g. "C::OWN::business_units",
 * which is what `metadata[<full_key>]` addresses.
 * @returns {Promise<{catalogues: Array<{key, full_key, label, field_label, source, has_children}>}>}
 */
export const eqlCatalogues = async (params = {}) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.get(eqlPath(tenant, "catalogues/"), {
    params,
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * A catalog's whole tree, flattened to { [valuePath]: { key, label, locales } }.
 * The map key is the value path EXACTLY as a report stores it
 * ("type::a::type::b"), so a stored value is localized with one lookup.
 *
 * Needed because getAllUsedCatalogs() (last_form_catalogues) prunes everything
 * below the first level, leaving deep values with no label to show. One request
 * and one DB query per catalog; the backend caps the walk and flags `truncated`.
 * @param {string} key - catalog alias, e.g. "RC-100"
 */
export const eqlCatalogueTree = async (key) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session || !key) return null;

  const resp = await apiCall.get(eqlPath(tenant, "catalogues/"), {
    params: { key, tree: 1 },
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * Tenant users, searched server-side (email / first / last name, icontains).
 * @param {{search?: string, limit?: number}} [opts] - limit capped at 1000 by the backend
 * @returns {Promise<{count: number, limit: number, offset: number,
 *   results: Array<{id, email, first_name, last_name, is_active}>}>}
 */
export const eqlUsers = async ({ search, limit = 50 } = {}) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const params = { limit };
  if (search) params.search = search;

  const resp = await apiCall.get(eqlPath(tenant, "users/"), {
    params,
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * Report IDs assigned to a user: the union of `assigned_reports` across that
 * user's Assignment rows (the model does not guarantee a single row per user).
 *
 * Assignment is not a report column, so the Builder's "assigned user" filter
 * reads this list and narrows the report query to `id in <ids>`. See
 * restrictToReportIds (Exports/Builder/utils/queryFilters).
 * @param {number} userId
 * @returns {Promise<number[]>} deduped IDs; empty when the user has none
 */
export const eqlAssignedReportIds = async (userId) => {
  const resp = await post({
    model: "Assignment",
    fields: ["assigned_reports"],
    filters: { field: "user__id", operator: "eq", value: userId },
    limit: EQL_MAX_LIMIT,
  });
  const ids = (resp?.data || []).flatMap((row) => row?.assigned_reports || []);
  // The ArrayField allows null entries; they point at no report.
  return [...new Set(ids.filter((id) => id != null))];
};

/* ---------- Saved Builder views ---------- */
// Dedicated per-user store, mirroring the reports filter-config pattern but on
// its own endpoint (/users/config/builder-views/) and store key, so Builder
// views never mix with reports' saved filters. The whole pivot state is kept
// verbatim in `state`; a saved view is { view_id, name, state }.

const builderViewsPath = (tenant) => `${tenant}/api/users/config/builder-views/`;

export const getBuilderViews = async () => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.get(builderViewsPath(tenant), {
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * @param {string} name
 * @param {Object} state - full Builder state, stored verbatim
 */
export const saveBuilderView = async (name, state) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.patch(
    builderViewsPath(tenant),
    { name, state },
    { headers: authHeaders(session) }
  );
  return resp.data;
};

export const deleteBuilderView = async (viewId) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.delete(builderViewsPath(tenant), {
    params: { view_id: viewId },
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * Resumen del estado de la conversación de las denuncias.
 * GET /{tenant}/api/reports/communication-summary/?year=YYYY
 *
 * Vive aquí y no en apiMessages porque comparte alcance con EQL: el backend
 * parte del mismo manager `Report.objects`, así que respeta la política de
 * visibilidad del usuario igual que el resto del dashboard.
 *
 * Los grupos NO son excluyentes entre sí — una denuncia puede estar esperando
 * respuesta y además tener notas internas — salvo `without_messages`, que sí lo
 * es respecto de los dos primeros.
 *
 * @param {number} [year] acota a las denuncias creadas ese año
 * @returns {Promise<{total: number, last_message_from_reporter: number,
 *                    awaiting_reporter_reply: number, with_internal_notes: number,
 *                    without_messages: number} | null>}
 */
export const getCommunicationSummary = async (year) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.get(
    `${tenant}/api/reports/communication-summary/`,
    { headers: authHeaders(session), params: year ? { year } : undefined }
  );
  return resp.data;
};
